import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, UserRole } from '@/types'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  isOffline: boolean
  
  // Actions
  setUser: (user: User | null) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setOffline: (offline: boolean) => void
  login: (user: User) => void
  logout: () => void
  updateUser: (updates: Partial<User>) => void
  
  // Getters
  hasRole: (roles: UserRole[]) => boolean
  isAdmin: () => boolean
  isEngineer: () => boolean
  isSupervisor: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      isOffline: false,

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      
      setLoading: (isLoading) => set({ isLoading }),
      
      setError: (error) => set({ error }),
      
      setOffline: (isOffline) => set({ isOffline }),

      login: (user) => set({
        user,
        isAuthenticated: true,
        isLoading: false,
        error: null
      }),

      logout: () => set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      }),

      updateUser: (updates) => {
        const { user } = get()
        if (user) {
          set({ user: { ...user, ...updates } })
        }
      },

      hasRole: (roles) => {
        const { user } = get()
        return user ? roles.includes(user.role) : false
      },

      isAdmin: () => {
        const { user } = get()
        return user?.role === 'admin'
      },

      isEngineer: () => {
        const { user } = get()
        return user?.role === 'engineer'
      },

      isSupervisor: () => {
        const { user } = get()
        return user?.role === 'supervisor'
      }
    }),
    {
      name: 'bsnl-auth-storage',
      partialize: (state) => ({ 
        user: state.user, 
        isAuthenticated: state.isAuthenticated 
      })
    }
  )
)

export default useAuthStore
